import type { ClientPositionView } from "@solomon-apps/shared";
import { useUsdvBalanceForAddress } from "@solomon-apps/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { useClientContextKey } from "@/hooks/useClientContextKey";
import { discoverClientHoldings, type DiscoveredPosition } from "@/services/positionDiscovery";

import { classifyPositionSource, resolveFindOutcome } from "../findPosition";
import type { PositionSourceDetection } from "../positionSourceDetection";
import { resolveAddressBalancePreview, resolveFoundPositionPreview, resolveHoldingsPreview } from "../stepFlowState";
import type { FoundPositionPreview } from "../steps";
import type { FindPositionStatus } from "../steps";

interface DiscoveryOptions {
    sourceDetection: PositionSourceDetection;
    connectedWalletAddress: string;
    sourceInput: string;
    existingPositions: ClientPositionView[];
    candidateSourceAddress: string | null;
}
export type DiscoveryOutcome =
    | { surfaceUuid: string | null }
    | { stopped: "superseded" | "unresolved" | "not-covered" | "already-registered" | "failed" };

interface UsePositionDiscoveryResult {
    matchedSurfaceUuid: string | null;
    findStatus: FindPositionStatus;
    addressPreview: FoundPositionPreview | null;
    foundPosition: FoundPositionPreview | null;
    ensureDiscovered: (direct?: boolean) => Promise<DiscoveryOutcome>;
    resetDiscovery: () => void;
}

export function usePositionDiscovery({
    sourceDetection,
    connectedWalletAddress,
    sourceInput,
    existingPositions,
    candidateSourceAddress
}: DiscoveryOptions): UsePositionDiscoveryResult {
    const contextKey = useClientContextKey();
    const sourceHolding = classifyPositionSource(sourceDetection, connectedWalletAddress);
    // Detection may still describe the previous input during source preflight.
    const sourceResolved =
        sourceInput.length > 0 &&
        sourceHolding !== null &&
        (sourceDetection.status === "eoa"
            ? sourceDetection.address === sourceInput
            : sourceDetection.status === "multisig-squads" &&
              (sourceDetection.multisig.address === sourceInput ||
                  sourceDetection.multisig.vaultAddress === sourceInput));
    const discoveryKey = JSON.stringify([
        contextKey,
        sourceInput,
        sourceResolved ? sourceHolding?.holdingAddress : null
    ]);
    const latestKey = useRef(discoveryKey);
    latestKey.current = discoveryKey;
    const [resultKey, setResultKey] = useState<string | null>(null);
    const [matchedSurfaceUuid, setMatchedSurfaceUuid] = useState<string | null>(null);
    const [findStatus, setFindStatus] = useState<FindPositionStatus>("idle");
    const [discovered, setDiscovered] = useState<DiscoveredPosition | null>(null);
    const [holdings, setHoldings] = useState<DiscoveredPosition[]>([]);
    const [lpDiscoveryComplete, setLpDiscoveryComplete] = useState(true);
    const findRunRef = useRef(0);
    const discoveryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlight = useRef<{ key: string; promise: Promise<DiscoveryOutcome> } | null>(null);
    // Stable identity: the verification hook resets discovery from an effect.
    const resetDiscovery = useCallback((): void => {
        inFlight.current = null;
        setResultKey(null);
        setMatchedSurfaceUuid(null);
        setFindStatus("idle");
        setDiscovered(null);
        setHoldings([]);
        setLpDiscoveryComplete(true);
        findRunRef.current += 1;
    }, []);
    // Verification reuses completed or in-flight discovery for this source.
    // Direct review remains an explicit fallback for unsupported address types.
    async function ensureDiscovered(direct = false): Promise<DiscoveryOutcome> {
        if (discoveryTimer.current !== null) {
            clearTimeout(discoveryTimer.current);
            discoveryTimer.current = null;
        }
        if (resultKey === discoveryKey && findStatus === "found") return { surfaceUuid: matchedSurfaceUuid };
        if (!sourceResolved && !direct) return { stopped: "unresolved" };
        const requestKey = JSON.stringify([discoveryKey, direct && !sourceResolved]);
        if (inFlight.current?.key === requestKey) return inFlight.current.promise;

        const run = ++findRunRef.current;
        setResultKey(discoveryKey);
        setFindStatus("finding");
        setDiscovered(null);
        setHoldings([]);
        setLpDiscoveryComplete(true);
        setMatchedSurfaceUuid(null);

        const promise = (async (): Promise<DiscoveryOutcome> => {
            try {
                const result = await discoverClientHoldings({
                    asset: "USDv",
                    address: sourceInput,
                    ...(direct && !sourceResolved ? { verificationMode: "direct-solomon" as const } : {})
                });
                const holdings = result.positions;
                if (findRunRef.current !== run || latestKey.current !== discoveryKey) return { stopped: "superseded" };

                const outcome = resolveFindOutcome({
                    holdings,
                    existingPositions,
                    candidateSourceAddress: candidateSourceAddress ?? sourceInput
                });
                if (outcome.kind !== "found") {
                    setFindStatus(outcome.kind);
                    return { stopped: outcome.kind };
                }
                setDiscovered(outcome.holding);
                setHoldings(outcome.holdings);
                setLpDiscoveryComplete(result.lpDiscovery?.complete ?? true);
                setMatchedSurfaceUuid(outcome.programSurfaceUuid);
                setFindStatus("found");
                return { surfaceUuid: outcome.programSurfaceUuid };
            } catch {
                if (findRunRef.current !== run || latestKey.current !== discoveryKey) return { stopped: "superseded" };
                setFindStatus("failed");
                return { stopped: "failed" };
            } finally {
                if (findRunRef.current === run) inFlight.current = null;
            }
        })();
        inFlight.current = { key: requestKey, promise };
        return promise;
    }

    const ensureLatest = useRef(ensureDiscovered);
    ensureLatest.current = ensureDiscovered;
    useEffect(() => {
        resetDiscovery();
        if (!sourceResolved) return;
        // Wait for address entry to settle; no wallet connection or monitoring
        // is required. The timer uses the latest function so a verify click can
        // share its request rather than starting a second Jachin scan.
        discoveryTimer.current = setTimeout(() => {
            void ensureLatest.current();
        }, 300);
        return (): void => {
            if (discoveryTimer.current !== null) clearTimeout(discoveryTimer.current);
            discoveryTimer.current = null;
            findRunRef.current += 1;
            inFlight.current = null;
        };
    }, [discoveryKey, sourceResolved, resetDiscovery]);

    const currentStatus = resultKey === discoveryKey ? findStatus : "idle";
    const currentSurfaceUuid = resultKey === discoveryKey ? matchedSurfaceUuid : null;

    // Read Address USDv through the same token-balance query as the positions
    // table. A classified source can show its balance before verification or
    // backend discovery. Squads sources resolve to their vault. Once discovery
    // succeeds, use the server's canonical Address holding, never an LP account.
    const holdingAddress =
        currentStatus === "found"
            ? (discovered?.instance.address ?? null)
            : sourceResolved
              ? (sourceHolding?.holdingAddress ?? null)
              : null;
    const {
        amount: liveHoldingUsdv,
        isLoading: isLoadingHoldingUsdv,
        hasFetched: hasFetchedHoldingUsdv,
        error: holdingUsdvError
    } = useUsdvBalanceForAddress(holdingAddress);

    // Wait for a settled read: confirmed empty wallets show $0.00; errors show
    // unavailable. The early balance does not establish a Program match, so
    // navigation and the receipt still require backend discovery and verification.
    const balanceRead = {
        liveUsdv: liveHoldingUsdv,
        isLoadingLiveUsdv: isLoadingHoldingUsdv,
        hasFetchedLiveUsdv: hasFetchedHoldingUsdv,
        holdingUsdvError
    };
    const addressPreview = holdingAddress === null ? null : resolveAddressBalancePreview(balanceRead);
    const discoveredAddressPreview = resolveFoundPositionPreview({
        ...balanceRead,
        findStatus: currentStatus,
        discovered
    });

    const foundPosition = discoveredAddressPreview
        ? resolveHoldingsPreview({ holdings, addressPreview: discoveredAddressPreview, lpDiscoveryComplete })
        : null;
    return {
        matchedSurfaceUuid: currentSurfaceUuid,
        findStatus: currentStatus,
        addressPreview,
        foundPosition,
        ensureDiscovered,
        resetDiscovery
    };
}
